import { createFileRoute } from '@tanstack/react-router'

import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../utils/api';
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { RefreshCwIcon } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

interface DailyData {
    date: string;
    revenue: number;
    costs: number;
}

interface HomeDashboardData {
    base_currency: string;
    start_date: string;
    end_date: string;
    daily_data: DailyData[];
    outstanding_payables: number;
    total_revenue: number;
    total_costs: number;
    previous_total_revenue: number;
    previous_total_costs: number;
    revenue_change_percent: number;
    costs_change_percent: number;
    generated_at: string;
}

interface VATByCategory {
    vat_rate: number;
    vat_amount: number;
}

interface VATPositionData {
    base_currency: string;
    year: number;
    month: number | null;
    quarter: number | null;
    start_date: string;
    end_date: string;
    outgoing_vat_by_category: VATByCategory[];
    total_outgoing_vat: number;
    incoming_vat_by_category: VATByCategory[];
    total_incoming_vat: number;
    net_vat_position: number;
    generated_at: string;
}

const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface Company {
    id: string;
    name: string;
    vat_reporting_period: 'monthly' | 'quarterly';
}

// Helper to get current quarter (1-4)
const getCurrentQuarter = () => Math.ceil((new Date().getMonth() + 1) / 3);

export default function HomePage() {
    const [dashboardData, setDashboardData] = useState<HomeDashboardData | null>(null);
    const [vatPositionData, setVatPositionData] = useState<VATPositionData | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { t } = useI18n();

    // Initialize with current year, month, and quarter
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // JavaScript months are 0-indexed
    const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    // Get the selected company's VAT reporting period
    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    const isQuarterly = selectedCompany?.vat_reporting_period === 'quarterly';

    const fetchVATPositionData = async (year: number, companyId: string, month?: number, quarter?: number) => {
        if (!companyId) {
            setVatPositionData(null);
            return;
        }
        try {
            let url = `/api/analytics/vat-position?year=${year}&company_id=${companyId}`;
            if (quarter !== undefined) {
                url += `&quarter=${quarter}`;
            } else if (month !== undefined) {
                url += `&month=${month}`;
            }
            const vatResponse = await apiFetch(url);

            if (vatResponse.ok) {
                const data = await vatResponse.json();
                setVatPositionData(data);
            } else {
                console.warn('Failed to fetch VAT position data');
            }
        } catch (error) {
            console.error('Error fetching VAT position data:', error);
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch dashboard data
            const dashboardResponse = await apiFetch('/api/analytics/home-dashboard');

            if (dashboardResponse.ok) {
                const data = await dashboardResponse.json();
                setDashboardData(data);
            } else {
                throw new Error('Failed to fetch dashboard data');
            }

            // Fetch companies for the selector
            const companiesResponse = await apiFetch('/api/companies/');
            if (companiesResponse.ok) {
                const companiesData: Company[] = await companiesResponse.json();
                setCompanies(companiesData);

                // Auto-select first company if available
                if (companiesData.length > 0 && !selectedCompanyId) {
                    const firstCompany = companiesData[0];
                    setSelectedCompanyId(firstCompany.id);

                    // Fetch VAT data based on the first company's reporting period
                    if (firstCompany.vat_reporting_period === 'quarterly') {
                        await fetchVATPositionData(selectedYear, firstCompany.id, undefined, selectedQuarter);
                    } else {
                        await fetchVATPositionData(selectedYear, firstCompany.id, selectedMonth, undefined);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Refetch VAT position data when year, month, quarter, or company changes
    useEffect(() => {
        if (!loading && selectedCompanyId) {
            if (isQuarterly) {
                fetchVATPositionData(selectedYear, selectedCompanyId, undefined, selectedQuarter);
            } else {
                fetchVATPositionData(selectedYear, selectedCompanyId, selectedMonth, undefined);
            }
        }
    }, [selectedYear, selectedMonth, selectedQuarter, selectedCompanyId, isQuarterly]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCwIcon className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </Layout>
        );
    }

    if (error || !dashboardData) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <p className="text-muted-foreground">{error || 'No data available'}</p>
                    <Button onClick={fetchDashboardData} variant="outline">
                        <RefreshCwIcon className="h-4 w-4 mr-2" />
                        Retry
                    </Button>
                </div>
            </Layout>
        );
    }

    // Prepare chart data
    const chartData = dashboardData.daily_data.map(day => ({
        name: formatDate(day.date),
        date: day.date,
        revenue: day.revenue,
        costs: day.costs,
    }));

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('navigation.dashboard', 'common')}</h1>
                    </div>
                    <Button onClick={fetchDashboardData} variant="outline" size="sm">
                        <RefreshCwIcon className="h-4 w-4 mr-2" />
                        {t('actions.refresh', 'common')}
                    </Button>
                </div>

                {/* Key Metrics Cards with Chart Backgrounds */}
                {/* Combined Revenue Analytics Card */}
                <div className="grid grid-cols-7 md:grid-cols-7 gap-6">
                    <Card className="col-span-4">
                        <CardHeader className="flex flex-row items-start justify-between border-b">
                            <CardTitle className="text-xl font-bold text-[#1e3a8a] tracking-tight">{t('homeDashboard.revenue30', 'common')}</CardTitle>
                            <div className="flex items-center gap-4 text-xs font-bold tracking-wide pt-1">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[#1e3a8a]"></div>
                                    <span className="text-[#1e3a8a]">{t('metrics.categories.revenue', 'common')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[#f97316]"></div>
                                    <span className="text-[#1e3a8a]">{t('metrics.categories.expense', 'common')}</span>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4">
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <CartesianGrid vertical={true} strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.4} />
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 12 }}
                                            dy={10}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 12 }}
                                            tickFormatter={(value) => `$${value}`}
                                        />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value: number) => [formatCurrency(value, dashboardData.base_currency), '']}
                                        />
                                        <Line
                                            type="stepAfter"
                                            dataKey="costs"
                                            stroke="#f97316"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            dot={false}
                                            activeDot={{ r: 6, fill: '#f97316' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="#1e3a8a"
                                            strokeWidth={3}
                                            dot={{ r: 6, strokeWidth: 2, fill: '#fff', stroke: '#1e3a8a' }}
                                            activeDot={{ r: 8, strokeWidth: 0, fill: '#1e3a8a' }}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {companies.length > 0 && (
                        <Card className="col-span-3">
                            <CardHeader className="border-b">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-lg font-semibold">{t('homeDashboard.vatPosition', 'common')}</CardTitle>
                                        {vatPositionData && (
                                            <p className="text-xs text-muted-foreground">
                                                {formatDate(vatPositionData.start_date)} - {formatDate(vatPositionData.end_date)}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        {/* Company selector - only show if multiple companies exist */}
                                        {companies.length > 1 && (
                                            <Select
                                                value={selectedCompanyId}
                                                onValueChange={(value) => setSelectedCompanyId(value)}
                                            >
                                                <SelectTrigger size="sm" className="w-[150px]">
                                                    <SelectValue placeholder={t('homeDashboard.selectCompany', 'common') || 'Select company'} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {companies.map((company) => (
                                                        <SelectItem key={company.id} value={company.id}>
                                                            {company.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                        <Select
                                            value={selectedYear.toString()}
                                            onValueChange={(value) => setSelectedYear(parseInt(value))}
                                        >
                                            <SelectTrigger size="sm" className="w-[90px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((year) => (
                                                    <SelectItem key={year} value={year.toString()}>
                                                        {year}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {/* Show quarter selector for quarterly companies, month selector for monthly */}
                                        {isQuarterly ? (
                                            <Select
                                                value={selectedQuarter.toString()}
                                                onValueChange={(value) => setSelectedQuarter(parseInt(value))}
                                            >
                                                <SelectTrigger size="sm" className="w-[100px]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="1">{t('homeDashboard.q1', 'common') || 'Q1 (Jan-Mar)'}</SelectItem>
                                                    <SelectItem value="2">{t('homeDashboard.q2', 'common') || 'Q2 (Apr-Jun)'}</SelectItem>
                                                    <SelectItem value="3">{t('homeDashboard.q3', 'common') || 'Q3 (Jul-Sep)'}</SelectItem>
                                                    <SelectItem value="4">{t('homeDashboard.q4', 'common') || 'Q4 (Oct-Dec)'}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Select
                                                value={selectedMonth.toString()}
                                                onValueChange={(value) => setSelectedMonth(parseInt(value))}
                                            >
                                                <SelectTrigger size="sm" className="w-[120px]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                                        <SelectItem key={month} value={month.toString()}>
                                                            {new Date(2000, month - 1, 1).toLocaleString('en-US', { month: 'long' })}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4">
                                {vatPositionData ? (
                                    <>
                                        {/* Outgoing VAT (Payable) */}
                                        <div>
                                            <h3 className="text-md font-bold text-muted-foreground mb-3">{t('homeDashboard.vatPayable', 'common')}</h3>
                                            {vatPositionData.outgoing_vat_by_category.length > 0 ? (
                                                <div className="space-y-2">
                                                    {vatPositionData.outgoing_vat_by_category.map((category, index) => (
                                                        <div key={index} className="flex justify-between items-center text-sm">
                                                            <span className="text-muted-foreground">{category.vat_rate}%:</span>
                                                            <span className="font-medium">
                                                                {formatCurrency(category.vat_amount, vatPositionData.base_currency)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <div className="flex justify-between items-center pt-2 border-t">
                                                        <span className="font-medium">Total:</span>
                                                        <span className="text-lg font-semibold">
                                                            {formatCurrency(vatPositionData.total_outgoing_vat, vatPositionData.base_currency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">{t('homeDashboard.noOutgoingVat', 'common')}</p>
                                            )}
                                        </div>

                                        {/* Incoming VAT (Deductible) */}
                                        <div>
                                            <h3 className="text-md font-bold text-muted-foreground mb-3 mt-3">{t('homeDashboard.vatDeductible', 'common')}</h3>
                                            {vatPositionData.incoming_vat_by_category.length > 0 ? (
                                                <div className="space-y-2">
                                                    {vatPositionData.incoming_vat_by_category.map((category, index) => (
                                                        <div key={index} className="flex justify-between items-center text-sm">
                                                            <span className="text-muted-foreground">{category.vat_rate}%:</span>
                                                            <span className="font-medium">
                                                                {formatCurrency(category.vat_amount, vatPositionData.base_currency)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <div className="flex justify-between items-center pt-2 border-t">
                                                        <span className="font-medium">Total:</span>
                                                        <span className="text-lg font-semibold">
                                                            {formatCurrency(vatPositionData.total_incoming_vat, vatPositionData.base_currency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">{t('homeDashboard.noIncomingVat', 'common')}</p>
                                            )}
                                        </div>

                                        {/* Net VAT Position */}
                                        <div className="pt-4 border-t-2">
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold">{t('homeDashboard.netVatPosition', 'common')}:</span>
                                                <span className={`text-xl font-bold ${vatPositionData.net_vat_position >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {formatCurrency(vatPositionData.net_vat_position, vatPositionData.base_currency)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {vatPositionData.net_vat_position >= 0
                                                    ? t('homeDashboard.amountPayable', 'common')
                                                    : t('homeDashboard.amountReclaimable', 'common')}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center py-8">
                                        <RefreshCwIcon className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/home')({
    component: HomePage,
})
